import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { KtInsightsFilterSelection, KtInsightsParameters } from '@/types';

/**
 * The report's own filters, from Embed.StaticFilter via the API — never a list in this file.
 *
 * The portal used to send two of the seven filters a KT Insights export actually carries, so a
 * scheduled savings report silently lost Currency Displayed and Period Type, both of which change
 * the numbers rather than the presentation. Everything offered here is what the estate says this
 * report can be sliced by; nothing ticked means the filter is not applied at all.
 *
 * **Shared by the KT Insights page and the report pack editor**, and extracted here for exactly
 * that reason. The pack editor previously initialised `filters` to `[]` and never made it editable,
 * so a KT Insights pack item could only be scoped by customer and period — the same gap this
 * component was written to close on the other screen. A second picker would have drifted from this
 * one the first time the estate added a filter type, and the drift would show up as an export
 * quietly carrying different filters depending on which screen scheduled it.
 */
export function KtInsightsFiltersEditor({ reportId, value, onChange }: {
  reportId: number | null;
  value: KtInsightsParameters;
  onChange: (p: KtInsightsParameters) => void;
}) {
  const api = useApi();

  const { data, isFetching } = useQuery({
    queryKey: ['ktinsights-report-filters', reportId],
    queryFn: () => api.ktInsightsReportFilters(reportId!),
    enabled: reportId !== null
  });

  const options = data?.filters ?? [];
  const selections = value.filters ?? [];

  // Rebuilt in catalogue order from the boxes that are ticked, so what is stored reads in the same
  // order it is shown and an untouched filter cannot linger as an empty selection.
  const setValues = (key: string, values: string[]) => {
    const next = options
      .map(o => {
        const chosen = o.key === key ? values : (selections.find(f => f.key === o.key)?.values ?? []);
        return chosen.length === 0 ? null : {
          key: o.key, label: o.label, table: o.table, column: o.column,
          operator: o.operator, values: chosen
        };
      })
      .filter((f): f is KtInsightsFilterSelection => f !== null);

    onChange({ ...value, filters: next.length === 0 ? null : next });
  };

  // A stored filter the report no longer offers has no checkbox, so without this it would be
  // invisible AND unremovable — and the save would fail on it with a message pointing at a control
  // that is not on screen. Same reason the PBIRS parameter editor keeps a removable row for a
  // stored value its report has dropped.
  const orphaned = options.length === 0 ? [] : selections.filter(f => !options.some(o => o.key === f.key));

  if (reportId === null) return null;

  if (isFetching && options.length === 0)
    return <div className="placeholder">Loading this report’s filters…</div>;

  // "Could not be read", not "has no filters" — the form must not present an unreachable MANDW01 as
  // a report that cannot be sliced, or someone schedules an unfiltered export believing it is the
  // only option available.
  if (data?.source === 'unavailable')
    return (
      <div className="notice notice-warn">
        KT Insights could not be reached, so the filters this report offers are not known. The
        export can still be scheduled with a head office and a date range.
      </div>
    );

  if (options.length === 0) return null;

  return (
    <div>
      <div style={{ fontSize: '.78rem', color: 'var(--kt-grey-500)', marginBottom: 6 }}>
        Report filters <span style={{ color: 'var(--kt-grey-400)' }}>— leave a filter unticked to include every value</span>
      </div>

      {orphaned.length > 0 && (
        <div className="notice notice-warn" style={{ marginBottom: 10 }}>
          {orphaned.map(f => f.label).join(', ')} {orphaned.length === 1 ? 'is' : 'are'} no longer
          offered on this report and will be refused when you save.{' '}
          <button className="btn-ghost btn-sm" onClick={() => {
            const kept = selections.filter(f => !orphaned.some(o => o.key === f.key));
            onChange({ ...value, filters: kept.length === 0 ? null : kept });
          }}>
            Remove
          </button>
        </div>
      )}

      {options.map(option => {
        const chosen = selections.find(f => f.key === option.key)?.values ?? [];

        return (
          <div className="param-field" key={option.key}>
            <label>{option.label}</label>
            <div className="param-choices" role="group" aria-label={option.label}>
              {option.values.map(v => (
                <label key={v} className="param-choice">
                  <input
                    type="checkbox"
                    checked={chosen.includes(v)}
                    onChange={e => setValues(
                      option.key,
                      e.target.checked ? [...chosen, v] : chosen.filter(c => c !== v)
                    )}
                  />
                  {v}
                </label>
              ))}
            </div>
            {/* Shown, never preselected: the KT Insights front-end applies this by default, so
                somebody comparing an emailed export against the web app wants to know which one
                they are looking at. Applying it silently would filter an export nobody asked to
                filter, which is the bug this whole section exists to fix. */}
            {option.defaultValues.length > 0 && chosen.length === 0 && (
              <div className="field-hint">
                KT Insights defaults this to {option.defaultValues.join(', ')}.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
